/**
 * Options of the "Наш аккаунт" filter of the clients list (KAN-618): our
 * Telegram accounts. Same query key and query function as the Telegram
 * accounts page, so the cache is shared. null: not loaded or failed (the
 * filter is hidden then).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { senderAccountName } from '@/lib/senderAccount';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import { useCanCallApi } from '@/hooks/useApiAccess';
import type { TelegramAccount } from '@/types/telegramAccount';

export interface SenderAccountOption {
  /** account_id sent as `sender_account`. */
  value: string;
  label: string;
}

/** Primary and decommissioned accounts are not offered. */
export function toSenderAccountOptions(
  accounts: TelegramAccount[]
): SenderAccountOption[] {
  const seen = new Set<string>();
  const options: SenderAccountOption[] = [];
  for (const account of accounts) {
    const id = account.account_id;
    if (!id || id === 'primary' || account.decommissioned || seen.has(id)) {
      continue;
    }
    seen.add(id);
    options.push({ value: id, label: senderAccountName(account) || id });
  }
  return options;
}

export function useSenderAccountOptions(): SenderAccountOption[] | null {
  // KAN-811: the accounts list is closed to the MANAGER role: no filter.
  const allowed = useCanCallApi('GET', '/api/v1/telegram-accounts/');
  const { data } = useQuery({
    queryKey: ['telegram-accounts'],
    queryFn: telegramAccountsApi.list,
    retry: false,
    enabled: allowed,
  });
  return useMemo(() => (data ? toSenderAccountOptions(data) : null), [data]);
}
